import { Component, effect, inject, OnDestroy, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { SubSink } from 'subsink';
import { ProfileApiService } from '../../apiCalls/profile-api.service';
import { AuthService } from '../../auth/auth.service';
import { CustomSnackbarService } from '../../shared/custom-snackbar.service';
import { RoutesConst } from '../../shared/route-const';

interface EditProfileForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  phoneNumber: FormControl<string>;
}

export interface EditProfileFormValue {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

@Component({
  selector: 'app-edit-profile',
  imports: [MatButtonModule, MatFormField, MatInputModule, ReactiveFormsModule],
  templateUrl: './edit-profile.component.html',
  styleUrl: './edit-profile.component.scss',
})
export class EditProfileComponent implements OnDestroy {
  private readonly subs = new SubSink();

  // Injected dependencies
  private readonly authSvc = inject(AuthService);
  private readonly profileApiSvc = inject(ProfileApiService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly customSnackbarSvc = inject(CustomSnackbarService);

  /** Reactive signals for state management */
  apiErrorMessage = signal<string>('');
  saveState = signal<boolean>(false);

  /** Form definition */
  form: FormGroup<EditProfileForm> = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    // phoneNumber: ["", [workPhoneValidator()]], // TODO: Without Validator 02.09.2025 2dg
    phoneNumber: [''],
  });

  constructor() {
    effect(() => {
      const data = this.authSvc.authentication().user;
      if (data) {
        this.form.patchValue({
          email: data.Email,
          firstName: data.FirstName,
          lastName: data.LastName,
          phoneNumber: data.PhoneNumber,
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();

    const payload: EditProfileFormValue = {
      ...formValue,
    };

    this.saveState.set(true);
    this.clearErrorMessage();

    this.customSnackbarSvc.requestSnackbar();
    this.subs.sink = this.profileApiSvc.updateUserProfile(payload).subscribe({
      next: () => this.handleSuccess(),
      error: (error) => this.handleError(error),
    });
  }

  /**
   * Handles successful
   */
  private handleSuccess(): void {
    this.clearErrorMessage();
    this.saveState.set(false);
    this.routeBack();
    this.customSnackbarSvc.successSnackbar('Thank you for updating your profile. It may take up to one business day to process the request.');
  }

  /**
   * Handles errors
   */
  private handleError(error: any): void {
    const errorMessage = error?.message || 'Unknown error occurred';
    this.apiErrorMessage.set(`: ${errorMessage}`);
    console.error('Error:', errorMessage);
    this.customSnackbarSvc.errorSnackbar();
    this.saveState.set(false);
  }

  routeBack() {
    this.router.navigate([RoutesConst.myProfile]);
  }

  /**
   * Clears the API error message
   */
  private clearErrorMessage(): void {
    this.apiErrorMessage.set('');
  }

  // Getters for easier access in the template
  get firstName() {
    return this.form.controls.firstName;
  }
  get lastName() {
    return this.form.controls.lastName;
  }
  get email() {
    return this.form.controls.email;
  }
  get phoneNumber() {
    return this.form.controls.phoneNumber;
  }
}
